import { useEffect, useRef } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ThemeSection } from './ThemeSection';
import { DEFAULT_WIDGET_THEME_CUSTOM } from './themePresets';
import { IndexPath } from '@/Config/app/router/index.path';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import { ImageUpload } from '@/Domain/components/ImageUpload';
import { usePreview } from '@/Domain/components/PreviewProvider';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/Domain/components/Shared/Item/Item';
import {
  RadioGroupSegmented,
  RadioGroupSegmentedItem,
} from '@/Domain/components/Shared/RadioGroup/RadioGroup';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Slider } from '@/Domain/components/Shared/Slider/Slider';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { UpgradeBadge } from '@/Domain/components/Shared/UpgradeBadge/UpgradeBadge';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { ThemeEnum } from '@/Domain/enum/Theme.enum';
import { WidgetLocationEnum } from '@/Domain/enum/WidgetLocation.enum';
import { WidgetSizeEnum } from '@/Domain/enum/WidgetSize.enum';
import { useUpdateWidgetSettings } from '@/Domain/hooks/Widget/useUpdateWidgetSettings.hook';
import { cn } from '@/Domain/utils/cn';

type Props = {
  data: WidgetSettingsDto;
};

export const DEFAULT_HEADER_COLOR = 'transparent';

// Keep in sync with LAUNCHER_LABEL_MAX_LENGTH in appquickchat/api/widget/schemas.py.
const LAUNCHER_LABEL_MAX_LENGTH = 25;

// Filename of the shared default avatar; an avatar_image pointing at it means "currently the default".
const DEFAULT_AVATAR_FILENAME = 'default_bubble.png';

/**
 * Legacy default values from the old widget before the customization expansion.
 * Widgets created before users could customize colors have these values in the database.
 * We map them to the new defaults so the color pickers start from a neutral state.
 */
export const LEGACY_WIDGET_DEFAULTS = {
  PRIMARY_COLOR: '#111827',
  HEADER_COLOR: 'rgb(3, 7, 18)',
} as const;

const SectionHeader = ({ label, testId }: { label: string; testId?: string }) => (
  <p
    data-testid={testId}
    className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle"
  >
    {label}
  </p>
);

const AppearanceFields = ({
  onUseDefault,
  defaultAvatarImage,
}: {
  onUseDefault: () => void;
  defaultAvatarImage: string;
}) => {
  const { t } = useTranslation(['widget', 'common', 'website']);
  const form = useFormContext<WidgetSettingsDto>();
  const { isActivated, isFetched } = useSubscription();
  const isWhiteLabelActive = !isFetched || isActivated('white_label');
  const isVoiceInputActive = !isFetched || isActivated('voice_input');

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader label={t('widget:branding')} testId="section-branding" />

      {/* AI Agent Avatar (header logo + launcher icon) */}
      <FormField
        name="avatar_image"
        render={({ field }) => {
          // The avatar is "the default" when it resolves to the shared default_bubble.png. Hide
          // "Reset to default" in that state; show it for a custom or a deleted (empty) avatar.
          const isDefaultAvatar = !!field.value && field.value.includes(DEFAULT_AVATAR_FILENAME);
          return (
            <ImageUpload
              testId="field-avatar_image"
              label={t(`${field.name}.label`)}
              description={t(`${field.name}.description`)}
              validation={t(`${field.name}.validation`)}
              upload_image={t(`${field.name}.upload_image`)}
              currentImage={field.value}
              onChange={(newImage) => field.onChange(newImage)}
              onUseDefault={isDefaultAvatar ? undefined : onUseDefault}
              use_default_label={t(`${field.name}.use_default`)}
              use_default_image={defaultAvatarImage}
            />
          );
        }}
      />

      <Separator />

      {/* Size & Position */}
      <SectionHeader
        label={t('website:appearance.form.size_position')}
        testId="section-size-position"
      />

      <FormField
        name="size"
        control={form.control}
        render={({ field }) => (
          <FormItem data-testid="field-size">
            <FormLabel>{t(`${field.name}.label`)}</FormLabel>
            <FormDescription>{t(`${field.name}.description`)}</FormDescription>
            <FormControl>
              <RadioGroupSegmented onValueChange={field.onChange} value={field.value}>
                {Object.values(WidgetSizeEnum).map((value) => (
                  <RadioGroupSegmentedItem
                    key={value}
                    value={value}
                    id={`size-${value}`}
                    data-testid={`size-${value}`}
                  >
                    {t(`${field.name}.${value}`)}
                  </RadioGroupSegmentedItem>
                ))}
              </RadioGroupSegmented>
            </FormControl>
          </FormItem>
        )}
      />

      {form.watch('size') === WidgetSizeEnum.SMALL && (
        <FormField
          name="launcher_label"
          render={({ field }) => (
            <FormItem data-testid="field-launcher_label">
              <FormLabel>{t('launcher_label.label')}</FormLabel>
              <FormDescription>{t('launcher_label.description')}</FormDescription>
              <FormControl>
                <Input
                  size="sm"
                  maxLength={LAUNCHER_LABEL_MAX_LENGTH}
                  placeholder={t('launcher_label.placeholder')}
                  {...field}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormDescription className="text-foreground-subtle">
                {t('launcher_label.translate_hint')}
              </FormDescription>
            </FormItem>
          )}
        />
      )}

      <FormField
        name="location"
        control={form.control}
        render={({ field }) => (
          <FormItem data-testid="field-location">
            <FormLabel>{t(`${field.name}.label`)}</FormLabel>
            <FormDescription>{t(`${field.name}.description`)}</FormDescription>
            <FormControl>
              <RadioGroupSegmented onValueChange={field.onChange} value={field.value}>
                {Object.values(WidgetLocationEnum).map((value) => (
                  <RadioGroupSegmentedItem
                    key={value}
                    value={value}
                    id={`location-${value}`}
                    data-testid={`location-${value}`}
                  >
                    {t(`${field.name}.${value}`)}
                  </RadioGroupSegmentedItem>
                ))}
              </RadioGroupSegmented>
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        name="window_height"
        control={form.control}
        rules={{
          required: t('window_height.rules.required'),
          min: { value: 400, message: t('window_height.rules.min') },
          max: { value: 1200, message: t('window_height.rules.max') },
        }}
        render={({ field, fieldState: { invalid } }) => (
          <FormItem data-testid="field-window_height">
            <FormLabel>{t(`${field.name}.label`)}</FormLabel>
            <div className="flex items-center gap-3">
              <FormControl className="flex-1">
                <Slider
                  id={field.name}
                  value={[field.value || 800]}
                  onValueChange={(values) => field.onChange(values[0])}
                  min={400}
                  max={1200}
                  step={10}
                  error={invalid}
                />
              </FormControl>
              <span className="text-sm text-foreground-subtle min-w-[48px] text-right">
                {field.value || 800}px
              </span>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        name="window_width"
        control={form.control}
        rules={{
          required: t('window_width.rules.required'),
          min: { value: 300, message: t('window_width.rules.min') },
          max: { value: 700, message: t('window_width.rules.max') },
        }}
        render={({ field, fieldState: { invalid } }) => (
          <FormItem data-testid="field-window_width">
            <FormLabel>{t(`${field.name}.label`)}</FormLabel>
            <div className="flex items-center gap-3">
              <FormControl className="flex-1">
                <Slider
                  id={field.name}
                  value={[field.value || 300]}
                  onValueChange={(values) => field.onChange(values[0])}
                  min={300}
                  max={700}
                  step={10}
                  error={invalid}
                />
              </FormControl>
              <span className="text-sm text-foreground-subtle min-w-[48px] text-right">
                {field.value || 300}px
              </span>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        name="launcher_draggable"
        render={({ field }) => (
          <FormItem className="flex items-center justify-between gap-4">
            <div className="space-y-1.5">
              <FormLabel>{t('website:appearance.form.launcher_draggable.title')}</FormLabel>
              <FormDescription>
                {t('website:appearance.form.launcher_draggable.description')}
              </FormDescription>
            </div>
            <FormControl>
              <Switch
                data-testid="launcher-draggable-switch"
                id={field.name}
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <Separator />

      {/* Theme */}
      <ThemeSection />

      <Separator />

      {/* Other Settings */}
      <SectionHeader
        label={t('website:appearance.form.other_settings')}
        testId="section-other-settings"
      />

      <FormField
        name="voice_active"
        disabled={!isVoiceInputActive}
        render={({ field }) => (
          <FormItem className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1.5 space-y-0">
            <FormLabel className="col-start-1 row-start-1">
              {t('website:appearance.form.voice_input.title')}
            </FormLabel>
            {!isVoiceInputActive && (
              <UpgradeBadge
                className="pointer-events-auto col-start-2 row-start-1 justify-self-end"
                data-testid="voice-input-upgrade-badge"
              />
            )}
            <FormDescription className="col-start-1 row-start-2">
              {t('website:appearance.form.voice_input.description')}
            </FormDescription>
            <div
              className={cn(
                'col-start-2 flex items-center justify-self-end',
                isVoiceInputActive ? 'row-span-2 row-start-1' : 'row-start-2',
              )}
            >
              <FormControl>
                <TooltipProvider>
                  <Tooltip open={isVoiceInputActive ? false : undefined}>
                    <TooltipTrigger asChild>
                      <div>
                        <Switch
                          data-testid="voice-active-switch"
                          id={field.name}
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!isVoiceInputActive}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>{t('higher_plan_required')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </FormControl>
            </div>
          </FormItem>
        )}
      />

      <FormField
        name="attachments_active"
        render={({ field }) => (
          <FormItem className="flex items-center justify-between gap-4">
            <div className="space-y-1.5">
              <FormLabel>{t('website:appearance.form.attachments.title')}</FormLabel>
              <FormDescription>
                {t('website:appearance.form.attachments.description')}
              </FormDescription>
            </div>
            <FormControl>
              <Switch
                data-testid="attachments-active-switch"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        name="sound_active"
        render={({ field }) => (
          <FormItem className="flex items-center justify-between gap-4">
            <div className="space-y-1.5">
              <FormLabel>{t('website:appearance.form.sound.title')}</FormLabel>
              <FormDescription>{t('website:appearance.form.sound.description')}</FormDescription>
            </div>
            <FormControl>
              <Switch
                data-testid="sound-active-switch"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        name="window_auto_open"
        render={({ field }) => (
          <FormItem className="flex items-center justify-between gap-4">
            <div className="space-y-1.5">
              <FormLabel>{t('website:appearance.form.auto_open.title')}</FormLabel>
              <FormDescription>
                {t('website:appearance.form.auto_open.description')}
              </FormDescription>
            </div>
            <FormControl>
              <Switch
                data-testid="auto-open-switch"
                id={field.name}
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        name="white_label"
        disabled={!isWhiteLabelActive}
        render={({ field }) => (
          <FormItem className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1.5 space-y-0">
            <FormLabel className="col-start-1 row-start-1">
              {t('website:appearance.form.remove_qc_brand.title')}
            </FormLabel>
            {!isWhiteLabelActive && (
              <UpgradeBadge
                className="pointer-events-auto col-start-2 row-start-1 justify-self-end"
                data-testid="white-label-upgrade-badge"
              />
            )}
            <FormDescription className="col-start-1 row-start-2">
              {t('website:appearance.form.remove_qc_brand.description')}
            </FormDescription>
            <div
              className={cn(
                'col-start-2 flex items-center justify-self-end',
                isWhiteLabelActive ? 'row-span-2 row-start-1' : 'row-start-2',
              )}
            >
              <FormControl>
                <TooltipProvider>
                  <Tooltip open={isWhiteLabelActive ? false : undefined}>
                    <TooltipTrigger asChild>
                      <div>
                        <Switch
                          data-testid="white-label-switch"
                          id={field.name}
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!isWhiteLabelActive}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>{t('higher_plan_required')}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </FormControl>
            </div>
          </FormItem>
        )}
      />

      <Separator />

      <Item
        data-testid="conversation-rating-section"
        variant="muted"
        className="w-full cursor-default"
        size="sm"
      >
        <ItemContent className="space-y-1.5">
          <ItemTitle>{t('website:conversation_rating.title')}</ItemTitle>
          <ItemDescription>{t('website:conversation_rating.description')}</ItemDescription>
        </ItemContent>
        <ItemActions>
          <Button data-testid="go-to-insights-link" variant="secondary" size="sm" asChild>
            <Link to={`../${IndexPath.insights}?tab=conversationRating`}>
              {t('website:appearance.form.go_to_insights')}
            </Link>
          </Button>
        </ItemActions>
      </Item>
    </div>
  );
};

export const AppearancePage = ({ data }: Props) => {
  const { reset, requestBubbleView } = usePreview();

  const form = useForm<WidgetSettingsDto>({
    values: {
      ...data,
      primary_color:
        !data.primary_color || data.primary_color === LEGACY_WIDGET_DEFAULTS.PRIMARY_COLOR
          ? ''
          : data.primary_color,
      header_color:
        data.header_color === LEGACY_WIDGET_DEFAULTS.HEADER_COLOR
          ? DEFAULT_HEADER_COLOR
          : data.header_color,
      window_auto_open: data.window_auto_open ?? false,
      streaming_active: data.streaming_active ?? false,
      white_label: data.white_label ?? false,
      header_collapse: data.header_collapse ?? false,
      launcher_draggable: data.launcher_draggable ?? false,
      voice_active: data.voice_active ?? false,
      attachments_active: data.attachments_active ?? true,
      sound_active: data.sound_active ?? true,
      launcher_label: data.launcher_label ?? '',
      theme: data.theme ?? ThemeEnum.Light,
      widget_theme_custom: data.widget_theme_custom ?? DEFAULT_WIDGET_THEME_CUSTOM,
      conversation_starters: data.conversation_starters ?? [],
    },
  });

  const { handleMutation } = useUpdateWidgetSettings({ form, reset });

  // Compare against the previously-seen size value rather than an
  // "is-initial-render" sentinel. The ref-based sentinel pattern survives
  // React Strict Mode's dev unmount/remount cycle and any react-query
  // refetch that re-pushes `values` into the form, both of which used to
  // spuriously fire `requestBubbleView()` on mount and overwrite caller-set
  // view modes (e.g. `/website?view=embed` from the post-onboarding deploy
  // CTA flipped back to bubble immediately).
  const sizeValue = form.watch('size');
  const prevSizeRef = useRef(sizeValue);
  useEffect(() => {
    if (sizeValue === prevSizeRef.current) return;
    prevSizeRef.current = sizeValue;
    requestBubbleView();
  }, [sizeValue, requestBubbleView]);

  return (
    <FormProvider {...form}>
      <form className="flex flex-col gap-8">
        <AppearanceFields
          onUseDefault={() => handleMutation({ use_default_avatar: true })}
          defaultAvatarImage={data.default_avatar_image}
        />
      </form>
    </FormProvider>
  );
};
